import React, { useState } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { searchPatients } from "../../../state/actions/workflow";
import Button from "../../Shared/Button";
import { formatDateTime } from "../../../utils";
import PatientResultsTable from "./PatientResultsTable";
import CoreplusTrack from "./CoreplusTrack";
import PmsTrack from "./PmsTrack";
import "./TrackPatients.css";

const isDataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";

/**
 * Searching for the patient is shared, but tracking is not: PMS tracks one referred
 * specialist at a time while CorePlus tracks the patient as a whole. A deployment only
 * talks to one of them, so exactly one of the two flows is mounted.
 */
const TrackPatients = () => {
  const [searchPatientName, setSearchPatientName] = useState("");
  const [patientRows, setPatientRows] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  const [failureReason, setFailureReason] = useState(null);

  const practitioners = useSelector((state) => state.app.practitioners);

  const handleSearchPatients = async () => {
    setIsSearchingPatients(true);
    setSelectedPatientId("");
    setFailureReason(null);
    const { hasError, errorText = "", patients = [] } = await searchPatients(searchPatientName);
    if (hasError) {
      setFailureReason(errorText);
    }
    const rows = patients.map((patient) => {
      const practitioner = _.find(
        practitioners,
        (practitioner) => practitioner.practitionerId === patient.practitionerId
      );
      const practitionerFullName = `${_.get(practitioner, "firstName", "")} ${_.get(practitioner, "lastName", "")}`;
      return {
        patientId: patient.clientId,
        fullname: `${patient.firstName} ${patient.lastName}`,
        dob: formatDateTime(patient.dateOfBirth, false, false),
        gender: _.get(patient, "gender", ""),
        practitionerFullName,
      };
    });
    setPatientRows(rows);
    // A single match is unambiguous, so it is pre-selected to save the extra click.
    if (rows.length === 1) {
      setSelectedPatientId(rows[0].patientId);
    }
    setIsSearchingPatients(false);
  };

  const handleSelectPatient = (patientId) => {
    setFailureReason(null);
    setSelectedPatientId(patientId);
  };

  return (
    <div className="is-size-7">
      <p className="menu-label">TRACK CLIENTS</p>
      <div className="field has-addons">
        <div className="control is-expanded">
          <input
            className="input is-radiusless is-size-7"
            type="text"
            placeholder="Search client by entering client's name here"
            value={searchPatientName}
            onChange={(e) => {
              setFailureReason(null);
              setSearchPatientName(e.target.value);
            }}
            onKeyPress={(e) => {
              if (e.which === 13) {
                handleSearchPatients();
              }
            }}
          />
        </div>
        <div className="control">
          <Button text="Search" onClick={handleSearchPatients} isLoading={isSearchingPatients} />
        </div>
      </div>
      <p className="has-text-danger">{failureReason}</p>
      <PatientResultsTable
        patientRows={patientRows}
        selectedPatientId={selectedPatientId}
        onSelectPatient={handleSelectPatient}
        isCompact={isDataFromPMS && Boolean(selectedPatientId)}
        showReferralHint={isDataFromPMS}
      />
      {isDataFromPMS ? (
        <PmsTrack clientId={selectedPatientId} onError={setFailureReason} />
      ) : (
        <CoreplusTrack clientId={selectedPatientId} onError={setFailureReason} />
      )}
    </div>
  );
};

export default TrackPatients;
